import IconAstronaut3 from '@apollo/icons/default/IconAstronaut3.svg';
import { Button } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useRef, useState } from 'react';

interface Props {
  subTitleText?: string;
  className?: string;
  onUpdate?: (file: File) => void;
}

export const UploadAvatar = ({ className, subTitleText, onUpdate }: Props) => {
  const fileUpload = useRef<HTMLInputElement>(null);
  const [fileUrl, setFileUrl] = useState<string>();
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const reader = new FileReader();
  reader.addEventListener('load', () => {
    // set the thumbnail preview for the picture
    const img = document.createElement('img');
    const canvas = document.createElement('canvas');
    if (!reader.result) {
      throw Error('');
    }
    img.onload = () => {
      canvas.width = Math.min(img.naturalHeight, img.naturalWidth);
      canvas.height = Math.min(img.naturalHeight, img.naturalWidth);
      const ctx = canvas.getContext('2d');

      const x = Math.floor(img.width / 2 - Math.min(img.width, img.height) / 2);
      const y = Math.floor(
        img.height / 2 - Math.min(img.width, img.height) / 2,
      );

      if (ctx) {
        // set background of image to white for uploads that don't
        // specify a background color
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(
          img,
          x,
          y,
          Math.min(img.naturalHeight, img.naturalWidth),
          Math.min(img.naturalHeight, img.naturalWidth),
          0,
          0,
          Math.min(img.naturalHeight, img.naturalWidth),
          Math.min(img.naturalHeight, img.naturalWidth),
        );
      }

      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Canvas is empty');
        }
        if (fileUrl) window.URL.revokeObjectURL(fileUrl);
        setFileUrl(window.URL.createObjectURL(blob));
      }, 'image/jpeg');
    };
    img.src = reader.result.toString();
  });

  return (
    <div className={classnames(className, 'text-base text-black')}>
      {subTitleText && <div className="mb-2">{subTitleText}</div>}
      <div
        className={classnames(
          'flex flex-col items-center rounded border border-primary',
          isDraggedOver && 'bg-secondary',
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDraggedOver(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDraggedOver(true);
        }}
        onDragEnd={(e) => {
          e.preventDefault();
          setIsDraggedOver(false);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDraggedOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDraggedOver(false);

          if (event.dataTransfer.files) {
            // pass file to the prop function
            // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (onUpdate) onUpdate(event.dataTransfer.files[0]!);
            // set thumbnail preview
            // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            reader.readAsDataURL(event.dataTransfer.files[0]!);
          }
        }}
      >
        <div className="mt-4 flex items-center justify-center rounded border border-primary bg-white shadow-soft">
          {fileUrl ? (
            <img src={fileUrl} alt="avatar" className="size-16 rounded" />
          ) : (
            <IconAstronaut3 className="m-4 text-icon-primary" />
          )}
        </div>
        <input
          className="hidden"
          type="file"
          id="avatar-upload"
          ref={fileUpload}
          onChange={(event) => {
            if (event.target.files) {
              // pass file to the prop function
              // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              if (onUpdate) onUpdate(event.target.files[0]!);
              // set thumbnail preview
              // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              reader.readAsDataURL(event.target.files[0]!);
            }
          }}
        />
        <Button
          className="m-4"
          variant="secondary"
          size="sm"
          onClick={() => fileUpload.current && fileUpload.current.click()}
        >
          Upload image
        </Button>
      </div>
    </div>
  );
};
